import React from 'react';
import { PrintableDocument, PrintStyles } from '../ui/PrintableDocument';
import { StatusBadge } from '../ui/StatusBadge';
import { formatCurrency, formatDate } from '../../lib/utils';

interface PrintableInvoiceProps {
  invoice: any;
  isVisible?: boolean;
}

export const PrintableInvoice: React.FC<PrintableInvoiceProps> = ({
  invoice,
  isVisible = false
}) => {
  if (!invoice) return null;

  return (
    <>
      <PrintStyles />
      <div className={`${isVisible ? 'block' : 'hidden'} print:block`}>
        <PrintableDocument type="invoice" data={invoice}>
          {/* Invoice Details Section */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            {/* Bill To */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                BILL TO
              </h3>
              <div className="space-y-2">
                <p className="font-semibold text-gray-900">{invoice.client?.name}</p>
                {invoice.client?.company && (
                  <p className="text-gray-700">{invoice.client.company}</p>
                )}
                <p className="text-gray-600">{invoice.client?.email}</p>
                {invoice.client?.phone && (
                  <p className="text-gray-600">{invoice.client.phone}</p>
                )}
                {invoice.client?.address && (
                  <p className="text-gray-600 whitespace-pre-line">{invoice.client.address}</p>
                )}
                {invoice.client?.gstin && (
                  <p className="text-gray-600">GSTIN: {invoice.client.gstin}</p>
                )}
              </div>
            </div>

            {/* Invoice Info */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                INVOICE DETAILS
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Invoice Date:</span>
                  <span className="font-medium">{formatDate(invoice.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Due Date:</span>
                  <span className="font-medium">{formatDate(invoice.due_date)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <StatusBadge status={invoice.status} />
                </div>
                {invoice.notes && (
                  <div className="mt-4">
                    <span className="text-gray-600 block mb-2">Notes:</span>
                    <p className="text-gray-700 text-sm bg-gray-50 p-3 rounded border">
                      {invoice.notes}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Invoice Items Table */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">ITEMS</h3>
            <table className="document-table w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left py-3 px-4 font-semibold">Description</th>
                  <th className="text-center py-3 px-4 font-semibold w-20">Qty</th>
                  <th className="text-right py-3 px-4 font-semibold w-24">Rate</th>
                  <th className="text-right py-3 px-4 font-semibold w-24">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.invoice_items?.map((item: any, index: number) => (
                  <tr key={item.id || index} className="border-b border-gray-100">
                    <td className="py-3 px-4">
                      <div className="font-medium text-gray-900">{item.description}</div>
                    </td>
                    <td className="py-3 px-4 text-center">{Number(item.quantity)}</td>
                    <td className="py-3 px-4 text-right">{formatCurrency(Number(item.rate))}</td>
                    <td className="py-3 px-4 text-right font-semibold">
                      {formatCurrency(Number(item.amount))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals Section */}
          <div className="flex justify-end">
            <div className="w-80">
              <div className="total-section bg-gray-50 border border-gray-200 rounded-lg p-6">
                <div className="space-y-3">
                  <div className="flex justify-between text-gray-700">
                    <span>Subtotal:</span>
                    <span className="font-medium">{formatCurrency(Number(invoice.subtotal))}</span>
                  </div>
                  <div className="flex justify-between text-gray-700">
                    <span>Tax ({Number(invoice.tax_rate)}%):</span>
                    <span className="font-medium">{formatCurrency(Number(invoice.tax_amount))}</span>
                  </div>
                  <div className="border-t border-gray-300 pt-3">
                    <div className="flex justify-between text-xl font-bold text-gray-900">
                      <span>Total:</span>
                      <span>{formatCurrency(Number(invoice.total))}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Terms */}
          <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">Payment Terms</h4>
            <p className="text-blue-800 text-sm">
              Payment is due within 30 days of invoice date. Late payments may incur additional charges.
              Please include the invoice number with your payment.
            </p>
          </div>
        </PrintableDocument>
      </div>
    </>
  );
};